import { BoxProps, Loader, ModBox, Spacer, Text } from '@jfs/ui-new'
import { getRandomAlphaNumString } from '@jfs/utils'
import { useIsFocused } from '@react-navigation/core'
import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { GestureHandlerGestureEvent } from 'react-native-gesture-handler'
import { CalendarTypes } from '../../comps/Calendar'
import CalendarViewModal from '../../comps/CalendarViewModal'
import Tabbar, { TabbarType, TabData } from '../../comps/Tabbar'
import {
  TitleSubtitleLayout,
  TitleSubtitleLayoutColorTheme,
  TitleSubtitleLayoutTypes
} from '../../comps/TitleSubtitleLayoutNew'
import { useLazyApiData, useStores } from '../../data/store'
import { getRangeDate, useWindowDimensions } from '../../util/util'

const stats = [
  { title: 'Today' },
  { title: 'This Week' },
  { title: 'This Month' },
  { title: 'Custom Dates', iconId: 'CALENDAR' },
]

export const Highlights = () => {
  const { setNewDate, date, setMessage, mid, fetchSummary, summary } = useStores((store) => ({
    setNewDate: store?.tokenizationRequests?.updateSummaryDates,
    setMessage: store?.response?.setMessage,
    date: store?.tokenizationRequests?.summarySelectedDate,
    mid: store?.user?.selectedMid,
    dateLabel: store?.tokenizationRequests?.summarySelectedDate?.dateLabel,
    fetchSummary: store.tokenizationRequests.getSummaryApi,
    summary: store.tokenizationRequests.getSummary,
  }))

  const viewPortWidth = useWindowDimensions()
  const isFocused = useIsFocused()

  const themedStyle = styles(viewPortWidth)

  const [tabValue, setTabValue] = useState(0)
  const [visible, setVisible] = useState(false)
  const [tabbarData, setTabbarData] = useState<TabData[]>(stats)
  const [layoutPos, setLayoutPos] = useState<any>('')
  const [calendarLayout, setCalendarLayout] = useState<any>('')
  const [calenderTopPos, setCalenderTopPos] = useState<any>(undefined)

  const [getSummaryApi, { state }] = useLazyApiData(fetchSummary)
  useEffect(() => {
    if (mid && isFocused) {
      getSummaryApi(mid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mid, isFocused, tabValue, tabbarData])

  const getDateValue = (dateValue: number): Date => {
    const today = new Date(Date.now())
    return new Date(today.setDate(today.getDate() - dateValue))
  }

  const getTodayValue = (): Date => {
    const today = new Date(Date.now())
    return new Date(today.setHours(0, 0, 0, 0))
  }

  const onCalendarLayout = (nativeEventData: GestureHandlerGestureEvent) => {
    if (calendarLayout === '') {
      setCalendarLayout(nativeEventData?.nativeEvent)
      layoutPos.target?.measure((...args: any) => {
        const top = args[5] - args[1] + 15
        setCalenderTopPos(top)
      })
    }
  }

  const close = (): void => {
    setVisible(false)
  }

  const onDateSelected = (firstdate: Date, secondDate?: Date): void => {
    if (secondDate) {
      const dateString = getRangeDate(firstdate, secondDate)
      const updatedStatistics = stats.slice()
      const items: TabData[] = []
      updatedStatistics.map((item) => {
        if (item.title === 'Custom Dates') {
          items.push({ title: dateString, iconId: item.iconId })
        } else {
          items.push(item)
        }
        return item
      })
      setTabbarData(items)
      setVisible(false)
      setNewDate({
        fromDate: firstdate,
        toDate: secondDate,
        dateLabel: 'custom',
      })
      setTabValue(3)
    }
  }

  const renderCalendarModal = (): ReactElement => {
    return (
      <ModBox {...styles(viewPortWidth).modal} show={visible}>
        <ModBox
          onPress={close}
          testID="close-calendar"
          underlayColor={'TRANSPARENT'}
          {...styles(viewPortWidth).overlayStyle}>
          <ModBox
            onLayout={onCalendarLayout}
            {...styles(viewPortWidth).dropdownStyle(calenderTopPos)}
            backgroundColor="AVAILABLE_GREEN">
            <CalendarViewModal
              onDateSelection={onDateSelected}
              dateRange={getRangeDate(date.fromDate, date.toDate)}
              type={CalendarTypes.HISTORY}
              onCancel={close}
              maxDuration={31}
              sendError={(err: string) => setMessage(err)}
            />
          </ModBox>
        </ModBox>
      </ModBox>
    )
  }

  const renderPortal = (): React.ReactPortal => {
    return ReactDOM.createPortal(renderCalendarModal(), document.body)
  }

  const getSelectedDate = useCallback((type: number): Date => {
    const getSelectedValue: any = {
      0: getTodayValue(),
      1: getDateValue(7),
      2: getDateValue(30),
    }
    return getSelectedValue[type]
  }, [])

  const handleSelected = (index: number): void => {
    if (index < 3) {
      setTabValue(index)
      const today = new Date(Date.now())
      const currentDay = today
      const finishDay = getSelectedDate(index)
      setNewDate({
        fromDate: finishDay,
        toDate: currentDay,
        dateLabel: 'specific',
      })
    } else {
      setTimeout(() => {
        setVisible(true)
      }, 300)
    }
  }

  const onLayout = (nativeEventData: any) => {
    setLayoutPos(nativeEventData?.nativeEvent)
    if (!calenderTopPos) {
      nativeEventData?.nativeEvent?.target?.measure((...args: any) => {
        const top = args[5] - args[1] + 15
        setCalenderTopPos(top)
      })
    }
  }

  const renderItem = ({ item }: { item: any }): JSX.Element => {
    return (
      <TitleSubtitleLayout
        type={TitleSubtitleLayoutTypes.BOLD_TITLE_TRENDS}
        placeHolder={item.title}
        color={TitleSubtitleLayoutColorTheme.BOLD_TITLE_TRENDS}
        title={item.subtitle}
        tooltip={item?.tooltip ?? ''}
        hideIcon={false}
        key={getRandomAlphaNumString(3)}
      />
    )
  }

  const getListView = (): ReactElement[] => {
    return summary().map((item: any) => {
      return renderItem({ item })
    })
  }

  // const loadingState = getLoadingState([apiHandler])
  // const errorState = getErrorMessage([apiHandler])

  return (
    <ModBox {...themedStyle.container} marginHorizontal={18}>
      {renderPortal()}
      <ModBox {...themedStyle.titleContainer}>
        <Text testID="vaultPageTitle" fontFamily="jiotype-medium" fontSize={20} color={'BLACK_21'}>
          {'Vault'}
        </Text>
        <Spacer direction={'vertical'} size={8} />
        <Tabbar
          default={tabValue}
          titles={tabbarData}
          onSelected={handleSelected}
          type={TabbarType.highlights}
          onLayoutOnlyForPosition={3}
          onLayout={onLayout}
        />
      </ModBox>
      <Spacer size={24} direction={'vertical'} />
      <ModBox justify={'center'} align={'center'} h={60} show={state === 'pending'}>
        {/* <EmptyView
          onRetry={handleRetry}
          isLoading={loadingState}
          error={errorState}
          emptyMessage={''}
          loadingMessage={'loading'}
          hide={!loadingState && !errorState}
        /> */}
        <Loader color={'BLUE'} />
      </ModBox>
      <ModBox show={state !== 'pending'} {...themedStyle.flexRow}>
        {getListView()}
      </ModBox>
    </ModBox>
  )
}

const styles = (breakpoint: number) => {
  let overRideFlexRow: BoxProps = {}

  const shadowStyle = {
    shadowOffset: { width: 1, height: 1 },
    shadowRadius: 3,
    shadowColor: '#0000001F',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#00000014',
    zIndex: 10,
  }

  if (breakpoint < 600 && breakpoint > 460) {
    overRideFlexRow = {
      justifyContent: 'flex-start',
    } as BoxProps
  }

  return {
    container: {
      ...shadowStyle,
      padding: 16,
      minHeight: 5,
      backgroundColor: 'WHITE',
      paddingBottom: 10,
      ph: 16,
    } as BoxProps,
    titleContainer: {
      flexDirection: 'row',
      align: 'center',
      justify: 'space-between',
    } as BoxProps,
    flexRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      ...overRideFlexRow,
    } as BoxProps,
    modal: {
      position: 'absolute',
      elevation: 2,
      shadowOpacity: 1,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      ...shadowStyle,
      zIndex: 1,
      borderWidth: 1,
      borderColor: 'GRAY_BORDER',
    } as BoxProps,
    overlayStyle: {
      backgroundColor: 'TRANSPARENT',
      width: '100%',
      height: '100%',
      justifyContent: 'center',
    } as BoxProps,
    dropdownStyle: (yAxisPos: number | undefined) =>
    ({
      position: 'absolute',
      shadowColor: 'BLACK_LIGHT',
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.3,
      shadowRadius: 10,
      elevation: 2,
      top: yAxisPos ? yAxisPos + 15 : undefined,
      borderRadius: 12,
      backgroundColor: 'BLUE',
      borderColor: 'SECONDARY_GREY',
      alignSelf: 'center',
      right: 32,
    } as BoxProps),
  }
}
